/* eslint-disable no-shadow */
/* eslint-disable no-console */
import Cookies from 'js-cookie';
import { LOCAL_STORAGE_KEYS } from 'dashboard/constants/localStorage';
import LicenseAPI from '../../api/license';

const isTrialStatus = status => ['trial', 'trialing'].includes(status);

const trialScopeKey = ({ planName, expiresAt } = {}) =>
  [planName || 'unknown-plan', expiresAt || 'unknown-expiry']
    .join(':')
    .toLowerCase();

const currentAuthSessionKey = trialScope => {
  try {
    const authData = JSON.parse(Cookies.get('cw_d_session_info') || '{}');
    return [
      LOCAL_STORAGE_KEYS.TRIAL_PAYWALL_DISMISSED,
      authData.uid || 'anonymous',
      authData.client || authData['access-token'] || 'session',
      trialScopeKey(trialScope),
    ].join(':');
  } catch {
    return `${LOCAL_STORAGE_KEYS.TRIAL_PAYWALL_DISMISSED}:anonymous:session:${trialScopeKey(trialScope)}`;
  }
};

const isTrialPaywallDismissed = trialScope =>
  window.localStorage.getItem(currentAuthSessionKey(trialScope)) === 'true';

const state = {
  valid: true,
  status: null,
  reason: null,
  startedAt: null,
  expiresAt: null,
  planName: null,
  blocked: false,
  blockedAt: null,
  blockMessage: '',
  installationIdentifier: '',
  plans: [],
  loadingPlans: false,
  plansUnavailable: false,
  hasValidated: false,
  paywallDismissed: false,
};

const getters = {
  isLicenseValid: $state => $state.valid,
  isLicenseBlocked: $state => $state.blocked,
  shouldShowPaywall: $state =>
    $state.hasValidated &&
    ($state.blocked ||
      ($state.valid &&
        isTrialStatus($state.status) &&
        !$state.paywallDismissed)),
  licenseStatus: $state => $state.status,
  currentPlanName: $state => $state.planName,
  licenseBlockMessage: $state => $state.blockMessage,
  installationIdentifier: $state => $state.installationIdentifier,
  availablePlans: $state => $state.plans,
  isLoadingPlans: $state => $state.loadingPlans,
  arePlansUnavailable: $state => $state.plansUnavailable,
  hasValidatedLicense: $state => $state.hasValidated,
};

const mutations = {
  SET_LICENSE_STATUS(
    state,
    {
      valid,
      status,
      reason,
      startedAt,
      expiresAt,
      planName,
      installationIdentifier,
    }
  ) {
    state.valid = valid;
    state.status = status;
    state.reason = reason;
    state.startedAt = startedAt;
    state.expiresAt = expiresAt;
    state.planName = planName;
    state.installationIdentifier =
      installationIdentifier || state.installationIdentifier;
    state.paywallDismissed = isTrialStatus(status)
      ? isTrialPaywallDismissed({ planName, expiresAt })
      : true;
    state.blocked = false;
    state.blockedAt = null;
    state.blockMessage = '';
    if (valid) state.reason = null;
  },
  SET_LICENSE_BLOCKED(state, { reason, message, installationIdentifier }) {
    state.blocked = true;
    state.blockedAt = new Date().toISOString();
    state.blockMessage = message;
    state.reason = reason;
    state.valid = false;
    state.installationIdentifier =
      installationIdentifier || state.installationIdentifier;
  },
  CLEAR_LICENSE_BLOCK(state) {
    state.blocked = false;
    state.blockedAt = null;
    state.blockMessage = '';
  },
  SET_LOADING_PLANS(state, status) {
    state.loadingPlans = status;
  },
  SET_PLANS(state, plans) {
    state.plans = plans;
  },
  DISMISS_PAYWALL(state) {
    state.paywallDismissed = true;
    window.localStorage.setItem(
      currentAuthSessionKey({
        planName: state.planName,
        expiresAt: state.expiresAt,
      }),
      'true'
    );
  },
  SET_PLANS_UNAVAILABLE(state, status) {
    state.plansUnavailable = status;
  },
  SET_LICENSE_VALIDATED(state, status) {
    state.hasValidated = status;
  },
};

const actions = {
  async validateLicense({ commit }) {
    commit('SET_LICENSE_VALIDATED', false);
    try {
      const { data } = await LicenseAPI.validate();
      if (data.blocked) {
        commit('SET_LICENSE_BLOCKED', {
          reason: data.code,
          message: data.message,
          installationIdentifier: data.installationIdentifier,
        });
      } else {
        commit('SET_LICENSE_STATUS', {
          valid: data.valid,
          status:
            data.license?.status || (data.license?.trial ? 'trialing' : null),
          reason: data.code,
          startedAt: data.license?.startedAt,
          expiresAt: data.license?.expiresAt,
          planName: data.plan?.name,
          installationIdentifier: data.installationIdentifier,
        });
      }
    } catch (error) {
      if (error.response?.status === 402 || error.response?.status === 403) {
        const data = error.response.data;
        commit('SET_LICENSE_BLOCKED', {
          reason: data.code || 'LICENSE_BLOCKED',
          installationIdentifier: data.installationIdentifier,
          message: data.message,
        });
      }
    } finally {
      commit('SET_LICENSE_VALIDATED', true);
    }
  },

  handleLicenseEvent({ commit }, { event, message }) {
    if (
      ![
        'LICENSE_RESTORED',
        'NETWORK_BLOCKED',
        'NETWORK_UNAVAILABLE',
        'LICENSE_EXPIRED',
        'TRIAL_EXPIRED',
      ].includes(event)
    ) {
      return;
    }

    commit('SET_LICENSE_VALIDATED', true);
    if (event === 'LICENSE_RESTORED') {
      commit('CLEAR_LICENSE_BLOCK');
    } else if (event === 'NETWORK_BLOCKED') {
      commit('CLEAR_LICENSE_BLOCK');
    } else if (event === 'NETWORK_UNAVAILABLE') {
      // Indisponibilidade de rede não é prova de licença inválida.
      // Apenas informar o estado — não limpar bloqueios existentes.
    } else if (['LICENSE_EXPIRED', 'TRIAL_EXPIRED'].includes(event)) {
      commit('SET_LICENSE_BLOCKED', { reason: event, message });
    }
  },

  async fetchPlans({ commit }) {
    commit('SET_LOADING_PLANS', true);
    commit('SET_PLANS_UNAVAILABLE', false);
    try {
      const { data } = await LicenseAPI.fetchPlans();
      commit('SET_PLANS', data);
    } catch (error) {
      console.error('Failed to fetch plans', error);
      commit('SET_PLANS_UNAVAILABLE', true);
    } finally {
      commit('SET_LOADING_PLANS', false);
    }
  },

  async upgradePlan({ dispatch }, planId) {
    try {
      await LicenseAPI.upgradePlan(planId);
      // Refresh license state
      await dispatch('validateLicense');
      return { success: true };
    } catch (error) {
      return {
        success: false,
        status: error.response?.status,
        error: error.response?.data?.error,
      };
    }
  },

  dismissPaywall({ commit }) {
    commit('DISMISS_PAYWALL');
  },
};

export default {
  namespaced: true,
  state,
  getters,
  mutations,
  actions,
};
